// ─── Tipos comunes ────────────────────────────────────────────────────────────

export interface HookState<T> {
	data: T | null;
	loading: boolean;
	error: string | null;
	cachedAt: string | null;
}

// ─── Recibos ─────────────────────────────────────────────────────────────────

export type ReceiptDepositStatus = 'EN_BORRADOR' | 'DEPOSITADO';

export interface IBranchReceiptsCurrencyTotal {
	transactionCurrency: string;
	totalTransactionAmount: number;
}

export interface IBranchReceiptItem {
	id: string;
	receiptNumber: string;
	issued: boolean;
	issuedAt: string | null;
	issuedBy: string | null;
	pdfUrl: string | null;
	passengerId: string | null;
	saleOrderId: string | null;
	dealId: string | null;
	transactionId: string | null;
	/** ⚠️ String numérico de la DB — convertir con Number(). Puede ser negativo (refunds). */
	transactionAmount: string;
	transactionCurrency: string;
	transactionAmountInLocalCurrency: string | null;
	/** ⚠️ String numérico de la DB — convertir con Number(). */
	amountInMainCurrency: string;
	relationType: string;
	platformType: string;
	observations: string | null;
	agencyId: number;
	createdAt: string;
	extBranchName: string | null;
	extCashSourceType: string | null;
	reconciled: boolean;
	reconciledAt: string | null;
	reconciledBy: string | null;
	/** null = libre para depositar · EN_BORRADOR = en acta sin confirmar · DEPOSITADO = acta confirmada */
	depositStatus: ReceiptDepositStatus | null;
	/** ID del acta al que pertenece (si depositStatus !== null). */
	depositId: number | null;
}

export interface IBranchReceiptsData {
	branch: string;
	receipts: IBranchReceiptItem[];
	pagination: {
		total: number;
		limit: number;
		offset: number;
		returned: number;
	};
	totals: {
		amountInMainCurrency: number;
		byTransactionCurrency: IBranchReceiptsCurrencyTotal[];
	};
}

export interface IBranchTotalsItem {
	branch: string;
	slug: string;
	total: number;
	amountInMainCurrency: number;
	byTransactionCurrency: IBranchReceiptsCurrencyTotal[];
}

export interface IBranchReceiptsTotalsData {
	branches: IBranchTotalsItem[];
	grandTotals: {
		branches: number;
		total: number;
		amountInMainCurrency: number;
	};
}

// ─── Caja chica ───────────────────────────────────────────────────────────────

export type CashMovementType = 'INGRESO' | 'EGRESO';

/** Caja física dentro de la sucursal: la chica (default) o la caja fuerte. */
export type CashBox = 'CHICA' | 'FUERTE';

/**
 * Las categorías manuales (las únicas que el UI puede crear/ver como manuales).
 * RESTO_DEPOSITO, RETIRO_A_BANCO y DEPOSITO_CAJA_FUERTE los genera solo el
 * sistema al confirmar un acta. RETIRO_CAJA_FUERTE es el retiro manual de la
 * caja fuerte (depósito especial).
 */
export type CashMovementCategory =
	| 'VUELTO'
	| 'GASTO'
	| 'PAGO_PROVEEDOR'
	| 'AJUSTE'
	| 'OTRO'
	| 'RESTO_DEPOSITO'
	| 'RETIRO_A_BANCO'
	| 'DEPOSITO_CAJA_FUERTE'
	| 'RETIRO_CAJA_FUERTE'
	| 'CAMBIO_MONEDA';

export const CASH_MOVEMENT_CATEGORY_LABELS: Record<CashMovementCategory, string> = {
	VUELTO: 'Vuelto a cliente',
	GASTO: 'Gasto de oficina',
	PAGO_PROVEEDOR: 'Pago a proveedor',
	AJUSTE: 'Ajuste',
	OTRO: 'Otro',
	RESTO_DEPOSITO: 'Resto de depósito',
	RETIRO_A_BANCO: 'Retiro al banco',
	DEPOSITO_CAJA_FUERTE: 'Ingreso por acta de depósito',
	RETIRO_CAJA_FUERTE: 'Retiro para depósito especial',
	CAMBIO_MONEDA: 'Cambio de moneda',
};

/** Categorías disponibles para movimientos de EGRESO manual (caja chica). */
export const EGRESO_CATEGORIES: CashMovementCategory[] = ['VUELTO', 'GASTO', 'PAGO_PROVEEDOR', 'AJUSTE', 'OTRO'];

/** Categorías disponibles para movimientos de INGRESO manual (caja chica). */
export const INGRESO_CATEGORIES: CashMovementCategory[] = ['AJUSTE', 'OTRO'];

/** Categorías disponibles para movimientos de EGRESO manual (caja fuerte). */
export const FUERTE_EGRESO_CATEGORIES: CashMovementCategory[] = ['RETIRO_CAJA_FUERTE', 'AJUSTE', 'OTRO'];

/** Categorías disponibles para movimientos de INGRESO manual (caja fuerte). */
export const FUERTE_INGRESO_CATEGORIES: CashMovementCategory[] = ['AJUSTE', 'OTRO'];

/** Categorías que puede seleccionar el TL al registrar un movimiento manual (todas). */
export const MANUAL_CASH_MOVEMENT_CATEGORIES: CashMovementCategory[] = [...EGRESO_CATEGORIES];

/**
 * Explicación de cuándo usar cada categoría (se muestra como helper text).
 */
export const CASH_MOVEMENT_CATEGORY_HELP: Record<CashMovementCategory, string> = {
	VUELTO: 'Plata que le devolviste al cliente como cambio de una transacción en efectivo.',
	GASTO: 'Gasto operativo del día a día: papelería, limpieza, café, delivery, etc.',
	PAGO_PROVEEDOR: 'Pago directo a un proveedor con efectivo de la caja (no por banco).',
	AJUSTE: 'Corrección de diferencia contable: sobrante o faltante detectado al arquear la caja.',
	OTRO: 'Cualquier movimiento que no encaje en las categorías anteriores. Describilo bien en la nota.',
	RESTO_DEPOSITO: 'Generado automáticamente al confirmar un acta de depósito.',
	RETIRO_A_BANCO: 'Generado automáticamente al retirar el exceso de caja chica al banco.',
	DEPOSITO_CAJA_FUERTE: 'Generado automáticamente al confirmar un acta con destino caja fuerte.',
	RETIRO_CAJA_FUERTE: 'Billetes que se retiran de la caja fuerte para llevarlos al depósito especial.',
	CAMBIO_MONEDA: 'Par de movimientos: sale una moneda y entra su equivalente en otra.',
};

export type DepositStatus = 'BORRADOR' | 'CONFIRMADO' | 'ANULADO';

/** Destino del acta: al banco (default) o a la caja fuerte de la sucursal. */
export type DepositDestination = 'BANCO' | 'CAJA_FUERTE';

export interface ICashBalance {
	currency: string;
	balance: number;
	movements: number;
	/** null si no hay objetivo configurado. */
	target: number | null;
	/** > 0 = caja por encima del objetivo → sugerir retiro. null si no hay target. */
	excess: number | null;
}

export interface ICashBalanceData {
	branch: string;
	balances: ICashBalance[];
}

export interface ICashMovement {
	id: number;
	branch: string;
	/** Caja física a la que pertenece el movimiento (CHICA default). */
	box: CashBox;
	currency: string;
	type: CashMovementType;
	category: CashMovementCategory;
	amount: number;
	note: string | null;
	saleOrderId: string | null;
	/** ≠ null en VUELTO → ya fue consumido por ese depósito. */
	depositId: number | null;
	/** ≠ null → este movimiento es un contra-movimiento de corrección. */
	reversesMovementId: number | null;
	registeredBy: string;
	createdAt: string;
}

export interface ICashMovementsData {
	movements: ICashMovement[];
	pagination: { total: number; limit: number; offset: number; returned: number };
}

export interface ICashDepositReceipt {
	receiptId: string;
	receiptNumber: string;
	saleOrderId: string | null;
	dealId: string | null;
	amount: number;
	currency: string;
	/** Estado live del recibo en el Power (se actualiza cuando admin concilia). */
	reconciled: boolean;
}

export interface ICashDepositEquation {
	formula: string;
	receiptsTotal: number;
	changeLentTotal: number;
	pettyCashKept: number;
	pettyCashWithdrawn: number;
	expectedDeposit: number;
	depositedAmount: number;
	/** Parte del reparto que entra a la caja fuerte. */
	vaultAmount: number;
}

export interface ICashDeposit {
	id: number;
	branch: string;
	currency: string;
	status: DepositStatus;
	/** Derivado del reparto: CAJA_FUERTE si todo va a la fuerte (legacy/informativo). */
	destination: DepositDestination;
	receiptsTotal: number;
	changeLentTotal: number;
	/** Vueltos que compensa el acta (selección explícita). null = acta legacy. */
	changeMovementIds: number[] | null;
	pettyCashKept: number;
	pettyCashWithdrawn: number;
	/** Monto al banco (puede ser 0 si todo va a la fuerte). */
	depositedAmount: number;
	/** Monto a la caja fuerte de la sucursal. */
	vaultAmount: number;
	bankReference: string | null;
	notes: string | null;
	annulReason: string | null;
	registeredBy: string;
	createdAt: string;
	confirmedAt: string | null;
	annulledAt: string | null;
	equation: ICashDepositEquation;
	receipts: ICashDepositReceipt[];
	movements: ICashMovement[];
}

export interface ICashDepositsData {
	deposits: ICashDeposit[];
	pagination: { total: number; limit: number; offset: number; returned: number };
}

export interface ICashTarget {
	branch: string;
	currency: string;
	targetAmount: number;
	updatedBy: string | null;
	updatedAt: string | null;
}

/** Un vuelto sin consumir, candidato a ser compensado por el acta. */
export interface IPendingChange {
	id: number;
	amount: number;
	saleOrderId: string | null;
	note: string | null;
	createdAt: string;
	/** true si su orden está entre los recibos del acta (se pre-selecciona). */
	matchesActa: boolean;
}

/**
 * Respuesta del preview del acta (POST /cash/deposits/preview): la ecuación
 * resuelta antes de crear el borrador. `cashOnHand` = recibos + vueltos
 * matcheados (el wizard lo recalcula con la selección del usuario).
 */
export interface ICashDepositPreview {
	branch: string;
	currency: string;
	receiptsTotal: number;
	/** Checklist de vueltos sin consumir de esa caja/moneda. */
	pendingChange: IPendingChange[];
	changeLentTotal: number;
	cashOnHand: number;
	/** Saldo actual de la caja chica en esa moneda (tope sugerido del retiro extra). */
	pettyCashBalance: number;
}

// ─── Errores de negocio de la API ────────────────────────────────────────────

/**
 * Error 422 del POST /cash/deposits cuando la ecuación del depósito no cierra.
 * Mostrá `message` y `breakdown` tal cual al usuario.
 */
export interface DepositValidationError {
	message: string;
	breakdown?: {
		receiptsTotal: number;
		changeLentTotal: number;
		pettyCashKept: number;
		pettyCashWithdrawn: number;
		expectedDeposit: number;
		depositedAmount: number;
		difference: number;
	};
}
